<template>
  <v-card class="elevation-10 document-card mb-6">
    <div class="specific-people-info-container">
      <h2 class="people-headings mb-0">
        {{ category.displayName }}
      </h2>
      <DocumentBadges :category="category" />
      <p class="card-description">{{ category.description }}</p>

      <a
        v-if="template"
        :href="template.href"
        target="_blank"
        rel="noopener"
        download
        class="template-download d-inline-flex align-center mt-3"
        data-test="download-template"
      >
        <v-icon size="18" color="primary" class="mr-2">{{
          icons.mdiDownloadOutline
        }}</v-icon>
        {{ template.label }}
      </a>

      <div v-if="hasFiles" class="files mt-4">
        <DocumentFileRow
          v-for="file in document.files"
          :key="file.id"
          :file="file"
          :read-only="!canDeleteDocuments"
          @file-deleted="$emit('refetch')"
        />
      </div>

      <DocumentOriginalLocationField
        v-if="showOriginalLocation"
        :document="document"
        @saved="$emit('refetch')"
      />

      <div
        v-if="hasFiles && (document.name || document.notes)"
        class="document-meta mt-3"
      >
        <p v-if="document.name" class="document-meta-row">
          <strong>Name:</strong> {{ document.name }}
        </p>
        <p v-if="document.notes" class="document-meta-row">
          <strong>Notes:</strong> {{ document.notes }}
        </p>
      </div>

      <div
        v-if="!hasFiles"
        class="dropzone d-flex align-center mt-4"
        data-test="open-upload"
        @click="onDropzoneClick"
      >
        <DocumentIconBadge icon="upload" tile class="mr-3" />
        <div class="flex-grow-1">
          <div class="dropzone-title">Click to upload</div>
          <div class="dropzone-hint">PDF, JPG or PNG · Up to 30 MB</div>
        </div>
        <DocumentUploadButton @click="onDropzoneClick" />
      </div>

      <div v-if="hasFiles" class="card-button-container no-border">
        <FunkButton
          :button-text="document.notes ? 'Edit note' : 'Add note'"
          @buttonClicked="showEdit = true"
        />
      </div>

      <div v-if="hasFiles" class="card-cta-row">
        <DocumentUploadButton
          label="Upload another"
          @click="showAddFiles = true"
        />
      </div>

      <UploadDocumentDialog
        v-if="showUpload"
        v-model="showUpload"
        :viewed-testator-id="viewedTestatorId"
        :document-type="category.type"
        :document-type-display-name="category.displayName"
        :description="category.description"
        :allow-multiple-files="true"
        @uploaded="onUploaded"
      />

      <UploadDocumentDialog
        v-if="showAddFiles && document"
        v-model="showAddFiles"
        :viewed-testator-id="viewedTestatorId"
        :document-type="category.type"
        :document-type-display-name="category.displayName"
        :description="category.description"
        :allow-multiple-files="true"
        :existing-document-id="document.id"
        @uploaded="onAddFilesUploaded"
      />

      <EditDocumentDialog
        v-if="showEdit && document"
        v-model="showEdit"
        :document="document"
        @saved="onEdited"
      />

      <DeleteDocumentDialog
        v-if="showDelete && document"
        v-model="showDelete"
        :document="documentForDelete"
        @deleted="onDeleted"
      />
    </div>
  </v-card>
</template>

<script>
  import { mdiDownloadOutline } from '@mdi/js';
  import DocumentFileRow from './DocumentFileRow.vue';
  import DocumentIconBadge from './DocumentIconBadge.vue';
  import DocumentBadges from './DocumentBadges.vue';
  import DocumentOriginalLocationField from './DocumentOriginalLocationField.vue';
  import UploadDocumentDialog from './UploadDocumentDialog.vue';
  import EditDocumentDialog from './EditDocumentDialog.vue';
  import DeleteDocumentDialog from './DeleteDocumentDialog.vue';
  import DocumentUploadButton from './DocumentUploadButton.vue';
  import FunkButton from '../guest/FunkButton.vue';
  import { documentDeletePermissionMixin } from '../../mixins/document-delete-permission';
  import { templateFor } from '../../constants/js/testator-documents';

  export default {
    name: 'DocumentPlaceholderCard',
    components: {
      DocumentFileRow,
      DocumentIconBadge,
      DocumentBadges,
      DocumentOriginalLocationField,
      UploadDocumentDialog,
      EditDocumentDialog,
      DeleteDocumentDialog,
      DocumentUploadButton,
      FunkButton,
    },
    mixins: [documentDeletePermissionMixin],
    props: {
      viewedTestatorId: { type: [Number, String], required: true },
      category: { type: Object, required: true },
      documents: { type: Array, default: () => [] },
    },
    data() {
      return {
        icons: { mdiDownloadOutline },
        showUpload: false,
        showAddFiles: false,
        showEdit: false,
        showDelete: false,
      };
    },
    computed: {
      document() {
        return this.documents[0] || null;
      },
      hasFiles() {
        return !!this.document && (this.document.files || []).length > 0;
      },
      template() {
        return templateFor(this.category.type);
      },
      showOriginalLocation() {
        return !!this.category.capturesOriginalLocation && this.hasFiles;
      },
      documentForDelete() {
        return this.document
          ? { ...this.document, displayName: this.category.displayName }
          : { displayName: this.category.displayName };
      },
    },
    methods: {
      onDropzoneClick() {
        if (this.document) this.showAddFiles = true;
        else this.showUpload = true;
      },
      onUploaded() {
        this.$emit('refetch');
      },
      onAddFilesUploaded() {
        this.$emit('refetch');
      },
      onEdited() {
        this.$emit('refetch');
      },
      onDeleted({ id }) {
        this.$emit('deleted', { id, displayName: this.category.displayName });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../constants/styles/people-overview-info-blocks.scss';
  @import '../../constants/styles/document-card.scss';

  .template-download {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-primary-base);
    text-decoration: none;
  }
  .template-download:hover {
    text-decoration: underline;
  }
</style>
